import { useEffect, useRef, useState, type ReactNode } from "react";
import * as THREE from "three";
import type { ScanElement, ScanRoomGroup } from "@sts/geometry";
import { formatArea, formatLength } from "./format.js";

/**
 * The scan, in 3D, in the browser.
 *
 * The phone proves a capture is usable on site; this is where the detailed work
 * happens on a real screen. Click any element to select it — the measurements
 * live outside this component, in `@sts/geometry`, so the viewer and the survey
 * report can never quote different numbers for the same wall.
 *
 * TRANSFORMS. `RoomPlanConverter.flatten` writes a column-major 4x4 exactly as
 * `THREE.Matrix4.fromArray` expects one, so the matrix is used as-is. That is
 * worth stating because getting it wrong does not throw — it silently scatters
 * the room, which looks like a bad scan rather than a bad decode.
 */

const COLOURS: Record<ScanElement["kind"], number> = {
  wall: 0xeeeeee,
  floor: 0xc8c8c8,
  window: 0x3b82f6,
  door: 0xf97316,
  opening: 0x22c55e,
  object: 0xb8ab99,
};

/** Openings sit proud of their wall so they are visible rather than z-fighting. */
const THICKNESS: Record<ScanElement["kind"], number> = {
  wall: 0.08,
  floor: 0.03,
  window: 0.12,
  door: 0.12,
  opening: 0.12,
  object: 0,
};

const SELECTED = 0x14b8a6;

/**
 * A comment pinned to the model (the portal's plan comments, migration 0043):
 * a numbered marker standing at a world point, Y up — see `@sts/geometry`'s
 * `commentPinWorldPoint` for where that point comes from.
 */
export interface ScanViewerPin {
  readonly id: string;
  readonly number: number;
  readonly position: { readonly x: number; readonly y: number; readonly z: number };
}

/** Marker size in world metres: readable across a room, small beside a wall. */
const PIN_SIZE_M = 0.32;

/** A numbered disc, drawn once per pin into a small canvas for its sprite. */
function pinTexture(label: string, selected: boolean): THREE.Texture | null {
  const canvas = document.createElement("canvas");
  canvas.width = 64;
  canvas.height = 64;
  const g = canvas.getContext("2d");
  if (!g) return null;
  g.beginPath();
  g.arc(32, 32, 28, 0, Math.PI * 2);
  g.fillStyle = "#c77700";
  g.fill();
  g.lineWidth = selected ? 6 : 4;
  g.strokeStyle = selected ? "#0f766e" : "#ffffff";
  g.stroke();
  g.fillStyle = "#ffffff";
  g.font = `bold ${label.length > 2 ? 22 : 28}px Helvetica, Arial, sans-serif`;
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText(label, 32, 34);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/** A bold axis letter for its arrow's tip. */
function letterTexture(label: string, colour: string): THREE.Texture | null {
  const canvas = document.createElement("canvas");
  canvas.width = 64;
  canvas.height = 64;
  const g = canvas.getContext("2d");
  if (!g) return null;
  g.fillStyle = colour;
  g.font = "bold 44px Helvetica, Arial, sans-serif";
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText(label, 32, 34);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/**
 * The drawing axes, as on the floor plan and in AutoCAD: X to the right along
 * the front of the building, Y away from the front, Z up. Stood at the front
 * left corner, just clear of the model, and never turned with it — they are
 * the axes the squared model now lies along.
 */
function axesGizmo(bounds: THREE.Box3, span: number): THREE.Group {
  const gizmo = new THREE.Group();
  const length = Math.max(0.5, Math.min(2, span * 0.18));
  const origin = new THREE.Vector3(bounds.min.x - length * 0.35, bounds.min.y, bounds.max.z + length * 0.35);
  const axes: readonly [string, THREE.Vector3, number, string][] = [
    ["X", new THREE.Vector3(1, 0, 0), 0xd93a2b, "#d93a2b"],
    ["Y", new THREE.Vector3(0, 0, -1), 0x1f9d55, "#1f9d55"],
    ["Z", new THREE.Vector3(0, 1, 0), 0x2f7fd1, "#2f7fd1"],
  ];
  for (const [label, dir, colour, css] of axes) {
    gizmo.add(new THREE.ArrowHelper(dir, origin, length, colour, length * 0.22, length * 0.12));
    const texture = letterTexture(label, css);
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({ ...(texture ? { map: texture } : { color: colour }), depthTest: false, transparent: true }),
    );
    sprite.position.copy(origin).addScaledVector(dir, length * 1.18);
    sprite.scale.set(length * 0.3, length * 0.3, 1);
    sprite.renderOrder = 11;
    gizmo.add(sprite);
  }
  gizmo.userData["axes"] = true;
  return gizmo;
}

export interface ScanViewerProps {
  readonly groups: readonly ScanRoomGroup[];
  /**
   * The turn the floor plan is drawn at, degrees from plan +X toward plan +Y
   * (`@sts/geometry` plan-orientation.ts): the model is turned the same way
   * about the vertical, so it stands squared to the X/Y/Z axes with its front
   * toward the camera — the 3D view and the plan the same way round.
   */
  readonly turnDeg?: number;
  readonly selectedId?: string;
  readonly hiddenIds: ReadonlySet<string>;
  readonly onSelect: (id: string | undefined) => void;
  /** Comments' pins, drawn over the model (never hidden behind a wall). */
  readonly pins?: readonly ScanViewerPin[];
  readonly selectedPinId?: string;
  /** A pin was clicked. Without it pins are drawn but not clickable. */
  readonly onPinSelect?: (id: string) => void;
}

export function ScanViewer({
  groups,
  turnDeg = 0,
  selectedId,
  hiddenIds,
  onSelect,
  pins,
  selectedPinId,
  onPinSelect,
}: ScanViewerProps): ReactNode {
  const mountRef = useRef<HTMLDivElement>(null);
  const [unavailable, setUnavailable] = useState(false);
  const meshesRef = useRef<Map<string, THREE.Mesh>>(new Map());
  const selectRef = useRef(onSelect);
  selectRef.current = onSelect;
  const sceneRef = useRef<THREE.Scene | null>(null);
  // The model's own group — turned, with the pins in it; the axes are not.
  const modelRef = useRef<THREE.Group | null>(null);
  const pinSpritesRef = useRef<THREE.Sprite[]>([]);
  const pinSelectRef = useRef(onPinSelect);
  pinSelectRef.current = onPinSelect;

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xeef1f4);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(50, 1, 0.05, 500);

    // WebGL is not guaranteed. It is off in some locked-down corporate
    // browsers, absent in headless environments, and disabled when a machine
    // falls back to software rendering. Without this guard the constructor
    // throws and takes the whole survey page down, so a designer who cannot see
    // the model also cannot read the measurements beside it — which they could
    // otherwise have used perfectly well.
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true });
    } catch {
      setUnavailable(true);
      return;
    }
    renderer.setPixelRatio(window.devicePixelRatio);
    mount.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0xffffff, 1.6));
    const key = new THREE.DirectionalLight(0xffffff, 1.5);
    key.position.set(4, 8, 6);
    scene.add(key);

    const meshes = new Map<string, THREE.Mesh>();
    const bounds = new THREE.Box3();
    // Plan (x, y) is world (x, z), so a plan turn of θ from +x toward +y is a
    // turn of −θ about world Y.
    const model = new THREE.Group();
    model.rotation.y = (-turnDeg * Math.PI) / 180;
    scene.add(model);
    modelRef.current = model;

    for (const group of groups) {
      for (const element of group.elements) {
        const { transform, dimensions } = element;
        if (!transform || transform.length < 16 || !dimensions) continue;
        const [width = 0, height = 0, depth = 0] = dimensions;
        if (width <= 0 || height <= 0) continue;

        const geometry = new THREE.BoxGeometry(
          width,
          height,
          element.kind === "object" ? Math.max(depth, 0.05) : THICKNESS[element.kind],
        );
        const material = new THREE.MeshLambertMaterial({
          color: COLOURS[element.kind],
        });
        const mesh = new THREE.Mesh(geometry, material);
        // Column-major, matching `Matrix4.fromArray` — see the file header.
        mesh.applyMatrix4(new THREE.Matrix4().fromArray([...transform]));
        mesh.matrixAutoUpdate = false;
        mesh.updateMatrix();
        mesh.userData["id"] = element.id;
        model.add(mesh);
        meshes.set(element.id, mesh);
      }
    }
    // Measured as turned: the framing and the axes follow the squared model.
    model.updateMatrixWorld(true);
    for (const mesh of meshes.values()) bounds.expandByObject(mesh);
    meshesRef.current = meshes;

    // Frame the model. Without this the camera sits at the world origin, which
    // is wherever the AR session began — often not in the room at all.
    const centre = bounds.getCenter(new THREE.Vector3());
    const size = bounds.getSize(new THREE.Vector3());
    const span = Math.max(size.x, size.y, size.z, 1);
    // From the front (+z, the bottom of the plan), a little to the right and
    // above: the front along X, as on the plan.
    camera.position.set(centre.x + span * 0.45, centre.y + span * 0.85, centre.z + span * 1.35);
    camera.lookAt(centre);
    const gizmo = meshes.size > 0 ? axesGizmo(bounds, span) : null;
    if (gizmo) scene.add(gizmo);

    // A small orbit implementation rather than a control library: drag to turn,
    // wheel to zoom, and nothing else this view needs.
    let radius = camera.position.distanceTo(centre);
    let theta = Math.atan2(
      camera.position.x - centre.x,
      camera.position.z - centre.z,
    );
    let phi = Math.acos((camera.position.y - centre.y) / radius);
    let dragging = false;
    let moved = false;
    let lastX = 0;
    let lastY = 0;

    const place = (): void => {
      camera.position.set(
        centre.x + radius * Math.sin(phi) * Math.sin(theta),
        centre.y + radius * Math.cos(phi),
        centre.z + radius * Math.sin(phi) * Math.cos(theta),
      );
      camera.lookAt(centre);
    };

    const onDown = (event: PointerEvent): void => {
      dragging = true;
      moved = false;
      lastX = event.clientX;
      lastY = event.clientY;
    };
    const onMove = (event: PointerEvent): void => {
      if (!dragging) return;
      const dx = event.clientX - lastX;
      const dy = event.clientY - lastY;
      if (Math.abs(dx) + Math.abs(dy) > 3) moved = true;
      lastX = event.clientX;
      lastY = event.clientY;
      theta -= dx * 0.008;
      // Clamped so the camera never passes through the poles and flips the
      // model upside down mid-drag.
      phi = Math.min(Math.PI - 0.05, Math.max(0.05, phi - dy * 0.008));
      place();
    };
    const onUp = (event: PointerEvent): void => {
      dragging = false;
      // A drag is not a click. Without this, turning the model deselects.
      if (moved) return;
      const rect = renderer.domElement.getBoundingClientRect();
      const pointer = new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
      );
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(pointer, camera);
      // A pin is drawn over everything, so a click on one is the pin's.
      const pinHit = pinSelectRef.current ? raycaster.intersectObjects(pinSpritesRef.current)[0] : undefined;
      const pinId = pinHit?.object.userData["pinId"];
      if (typeof pinId === "string") {
        pinSelectRef.current?.(pinId);
        return;
      }
      const hits = raycaster.intersectObjects(
        [...meshes.values()].filter((m) => m.visible),
      );
      const id = hits[0]?.object.userData["id"];
      selectRef.current(typeof id === "string" ? id : undefined);
    };
    const onWheel = (event: WheelEvent): void => {
      event.preventDefault();
      radius = Math.min(span * 6, Math.max(span * 0.15, radius + event.deltaY * 0.01));
      place();
    };

    const element = renderer.domElement;
    element.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    element.addEventListener("wheel", onWheel, { passive: false });

    const resize = (): void => {
      const { clientWidth, clientHeight } = mount;
      if (clientWidth === 0 || clientHeight === 0) return;
      // `updateStyle` LEFT AT ITS DEFAULT (true) ON PURPOSE — do not pass
      // `false` here.
      //
      // `setPixelRatio(devicePixelRatio)` above means the drawing buffer is
      // deliberately larger than the CSS box: on a 2x display a 669x562
      // container gets a 1338x1124 buffer. `setSize(w, h)` sets BOTH — the
      // buffer to w*dpr x h*dpr and `canvas.style` to w x h CSS px — which is
      // what keeps the two in step.
      //
      // Passing `false` suppresses the style half, and a canvas with no
      // inline style falls back to its `width`/`height` ATTRIBUTES for
      // layout. Those attributes are the buffer size. So the canvas laid
      // itself out at exactly `devicePixelRatio` times its container,
      // overflowed, got clipped, and the model — framed at the centre of the
      // canvas — appeared in the bottom-right corner of the visible area
      // with no way to drag it back, because nothing was actually wrong with
      // the camera.
      //
      // That only worked before because a stylesheet elsewhere happened to
      // carry a `canvas { width: 100%; height: 100% }` rule; the portal's
      // design overhaul on 2026-09-09 replaced that stylesheet and the
      // viewer broke. Owning its own canvas sizing is what makes this
      // component correct regardless of whose CSS is loaded around it.
      renderer.setSize(clientWidth, clientHeight);
      camera.aspect = clientWidth / clientHeight;
      camera.updateProjectionMatrix();
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(mount);

    let frame = 0;
    const draw = (): void => {
      frame = requestAnimationFrame(draw);
      renderer.render(scene, camera);
    };
    draw();

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      element.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      element.removeEventListener("wheel", onWheel);
      for (const mesh of meshes.values()) {
        mesh.geometry.dispose();
        (mesh.material as THREE.Material).dispose();
      }
      gizmo?.traverse((o) => {
        if (o instanceof THREE.Sprite) {
          o.material.map?.dispose();
          o.material.dispose();
        } else if ("dispose" in o && typeof (o as { dispose?: unknown }).dispose === "function") {
          (o as unknown as { dispose: () => void }).dispose();
        }
      });
      renderer.dispose();
      element.remove();
      if (sceneRef.current === scene) sceneRef.current = null;
      if (modelRef.current === model) modelRef.current = null;
    };
  }, [groups, turnDeg]);

  // Pins are laid over the scene without rebuilding it, as selection is.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || !pins || pins.length === 0) return;
    const layer = new THREE.Group();
    const sprites: THREE.Sprite[] = [];
    for (const pin of pins) {
      const { x, y, z } = pin.position;
      if (![x, y, z].every(Number.isFinite)) continue;
      const texture = pinTexture(String(pin.number), pin.id === selectedPinId);
      const material = new THREE.SpriteMaterial({
        ...(texture ? { map: texture } : { color: 0xc77700 }),
        depthTest: false,
        transparent: true,
      });
      const sprite = new THREE.Sprite(material);
      sprite.position.set(x, y, z);
      sprite.scale.set(PIN_SIZE_M, PIN_SIZE_M, 1);
      sprite.renderOrder = 10;
      sprite.userData["pinId"] = pin.id;
      layer.add(sprite);
      sprites.push(sprite);
    }
    // In the model's group, so the pins turn with the walls they stand by.
    const parent = modelRef.current ?? scene;
    parent.add(layer);
    pinSpritesRef.current = sprites;
    return () => {
      parent.remove(layer);
      pinSpritesRef.current = [];
      for (const sprite of sprites) {
        sprite.material.map?.dispose();
        sprite.material.dispose();
      }
    };
  }, [pins, selectedPinId, groups, turnDeg]);

  // Selection and visibility are applied WITHOUT rebuilding the scene, so
  // clicking a wall does not throw the camera back to its starting angle
  // mid-inspection.
  useEffect(() => {
    for (const [id, mesh] of meshesRef.current) {
      mesh.visible = !hiddenIds.has(id);
      const material = mesh.material as THREE.MeshLambertMaterial;
      const kind = kindOf(id);
      material.color.setHex(id === selectedId ? SELECTED : (kind ?? 0xeeeeee));
    }

    function kindOf(id: string): number | undefined {
      for (const group of groups) {
        const found = group.elements.find((element: ScanElement) => element.id === id);
        if (found) return COLOURS[found.kind];
      }
      return undefined;
    }
  }, [selectedId, hiddenIds, groups]);

  if (unavailable) {
    return (
      <div data-testid="scan-viewer" role="note">
        <p>
          The 3D model cannot be shown because this browser has no WebGL. Every
          measurement is still listed beside it.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 360,
      }}
    >
      <div
        ref={mountRef}
        data-testid="scan-viewer"
        style={{ width: "100%", height: "100%", cursor: "grab" }}
      />
      <SelectedReadout groups={groups} selectedId={selectedId} />
    </div>
  );
}

/**
 * What you just clicked, and how big it is — pinned inside the viewer.
 *
 * Asked for directly on 2026-09-09: "as soon as I click on any wall, the
 * system on the same screen should show me how much is the actual size for
 * that wall". Selecting an element already highlighted its row in the
 * measurement tables, but those sit below the 3D view and are off-screen at
 * a normal laptop height — so the answer to "how big is this wall" required
 * clicking, then scrolling away from the thing you clicked. This puts the
 * number where the question was asked.
 *
 * It lives in the VIEWER rather than in the portal's survey page because
 * "what is selected in the 3D scene" is the viewer's own state to explain,
 * and because both the installer portal and the customer portal mount this
 * component — a homeowner looking at their own scan has the same question.
 *
 * Numbers come from the same `ScanElement` fields and the same formatters
 * (`formatArea`/`formatLength`) the tables use, so the card and the table
 * can never disagree about a measurement — which would be worse than
 * showing nothing on a document that gets audited.
 *
 * Exported so the portal's 2D floor plan shows the SAME card for the same
 * selection: a wall picked on the plan and a wall picked in 3D should not be
 * described in two different layouts.
 */
export function SelectedReadout({
  groups,
  selectedId,
}: {
  readonly groups: readonly ScanRoomGroup[];
  readonly selectedId?: string;
}): ReactNode {
  if (!selectedId) return null;

  let element: ScanElement | undefined;
  let roomLabel = "";
  for (const group of groups) {
    const found = group.elements.find((e) => e.id === selectedId);
    if (found) {
      element = found;
      roomLabel = found.roomLabel;
      break;
    }
  }
  // A selection the current groups don't contain — the survey reloaded, or
  // the selection belongs to a room that has since been filtered out. Say
  // nothing rather than showing a card with blanks in it.
  if (!element) return null;

  const hasOpenings = element.openingsAreaM2 > 0;

  return (
    <div
      data-testid="scan-viewer-readout"
      style={{
        position: "absolute",
        left: 12,
        bottom: 12,
        // Only the card takes pointer events; the rest of the overlay area
        // stays transparent to drags so orbiting the model still works with
        // the card on screen.
        pointerEvents: "auto",
        maxWidth: 260,
        padding: "10px 12px",
        borderRadius: 10,
        // Self-contained styling on purpose: this component is consumed by
        // two different apps with two different stylesheets, and it has
        // already been broken once (5cd31e5) by depending on a CSS rule
        // that lived somewhere else.
        background: "rgba(17, 24, 34, 0.88)",
        color: "#f4f7fa",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        boxShadow: "0 6px 24px rgba(0,0,0,0.28)",
        fontSize: 13,
        lineHeight: 1.35,
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          opacity: 0.62,
        }}
      >
        {roomLabel}
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, marginTop: 1 }}>
        {element.label}
      </div>

      <div
        style={{
          marginTop: 8,
          fontSize: 20,
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatLength(element.widthM, "m")} &times;{" "}
        {formatLength(element.heightM, "m")}
      </div>

      <div
        style={{
          marginTop: 4,
          fontVariantNumeric: "tabular-nums",
          opacity: 0.9,
        }}
      >
        {formatArea(element.areaM2, "m")}
        {hasOpenings ? " gross" : ""}
      </div>

      {hasOpenings ? (
        <div
          style={{
            marginTop: 2,
            fontVariantNumeric: "tabular-nums",
            opacity: 0.72,
            fontSize: 12,
          }}
        >
          &minus; {formatArea(element.openingsAreaM2, "m")} openings ={" "}
          <strong style={{ opacity: 1 }}>
            {formatArea(element.netAreaM2, "m")}
          </strong>{" "}
          net
        </div>
      ) : null}
    </div>
  );
}
